/**
 * Line diff for the "Mentés (felülírás)" check: the common head and tail are cut first, the middle
 * goes through an LCS table. A middle too big for the table shows as removed, then added.
 */

export type TDiffLine =
    | { kind: 'same' | 'added' | 'removed'; text: string }
    | { kind: 'skip'; count: number };

const MAX_CELLS = 4_000_000;

function middle(a: string[], b: string[]): TDiffLine[] {
    const n = a.length;
    const m = b.length;
    if (n * m > MAX_CELLS) {
        return [...a.map((text) => ({ kind: 'removed' as const, text })), ...b.map((text) => ({ kind: 'added' as const, text }))];
    }
    // lcs[i * (m + 1) + j]: the LCS length of a[i..] and b[j..]
    const lcs = new Uint32Array((n + 1) * (m + 1));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            lcs[i * (m + 1) + j] = a[i] === b[j]
                ? lcs[(i + 1) * (m + 1) + j + 1] + 1
                : Math.max(lcs[(i + 1) * (m + 1) + j], lcs[i * (m + 1) + j + 1]);
        }
    }
    const out: TDiffLine[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            out.push({ kind: 'same', text: a[i] });
            i++;
            j++;
        } else if (lcs[(i + 1) * (m + 1) + j] >= lcs[i * (m + 1) + j + 1]) {
            out.push({ kind: 'removed', text: a[i++] });
        } else {
            out.push({ kind: 'added', text: b[j++] });
        }
    }
    while (i < n) out.push({ kind: 'removed', text: a[i++] });
    while (j < m) out.push({ kind: 'added', text: b[j++] });
    return out;
}

/** The changes from `before` to `after`, with `context` unchanged lines around each; longer unchanged runs fold into a skip. */
export function diffLines(before: string, after: string, context = 3): TDiffLine[] {
    const a = before.split('\n');
    const b = after.split('\n');
    let head = 0;
    while (head < a.length && head < b.length && a[head] === b[head]) head++;
    let tail = 0;
    while (tail < a.length - head && tail < b.length - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail++;

    const all: TDiffLine[] = [
        ...a.slice(0, head).map((text) => ({ kind: 'same' as const, text })),
        ...middle(a.slice(head, a.length - tail), b.slice(head, b.length - tail)),
        ...a.slice(a.length - tail).map((text) => ({ kind: 'same' as const, text })),
    ];

    const out: TDiffLine[] = [];
    let run: TDiffLine[] = [];
    const flush = (first: boolean, last: boolean) => {
        const keepHead = first ? 0 : context;
        const keepTail = last ? 0 : context;
        if (run.length > keepHead + keepTail + 1) {
            out.push(...run.slice(0, keepHead), { kind: 'skip', count: run.length - keepHead - keepTail },
                ...run.slice(run.length - keepTail));
        } else {
            out.push(...run);
        }
        run = [];
    };
    let first = true;
    for (const line of all) {
        if (line.kind === 'same') {
            run.push(line);
        } else {
            flush(first, false);
            first = false;
            out.push(line);
        }
    }
    flush(first, true);
    return out;
}
